import { Routes } from '@angular/router';
import { accessGuard } from './core/guards/access.guard';
import { postAuthGuard } from './core/guards/post-auth.guard';
import { providerMatchGuard } from './core/guards/profile/provider-match.guard';
import { providerAccessGuard } from './core/guards/profile/provider-access.guard';
import { clinicDemographicsResolver } from './core/resolvers/clinic/clinic-demographics.resolver';
import { clinicAdminResolver } from './core/resolvers/clinic/clinic-admin.resolver';
import { providersByClinicResolver } from './core/resolvers/provider/providers-by-clinic.resolver';
import { providerDocumentResolver } from './core/resolvers/provider/provider-document.resolver';
import { providerOnboardingAccessGuard } from './core/guards/provider-onboarding/provider-onboarding-access.guard';
import { onboardingDemographicsMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-demographics-match.guard';
import { onboardingLocationsMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-locations-match.guard';
import { onboardingProvidersMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-providers-match.guard';
import { onboardingFeeScheduleMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-fee-schedule-match.guard';
import { onboardingDocumentsMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-documents-match.guard';
import { onboardingCompleteMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-complete-match.guard';
import { onboardingPendingMatchGuard } from './core/guards/provider-onboarding/provider-onboarding-steps/onboarding-pending-match.guard';
import { memberMatchGuard } from './core/guards/member/member-match.guard';
import { memberOnboardingMatchGuard } from './core/guards/member/member-onboarding-match.guard';
import { getMemberResolver } from './core/resolvers/member/get-member.resolver';
import { memberOnboardingAccessGuard } from './core/guards/member/member-onboarding-access.guard';
import { getAllClinicsResolver } from './core/resolvers/admin/get-all-clinics.resolver';
import { getAllProvidersResolver } from './core/resolvers/admin/get-all-providers.resolver';
import { getAllProivdersResolver as _getAllProvidersResolver } from './core/resolvers/provider/get-all-proivders.resolver'
import { getDocumentsByClinicResolver } from './core/resolvers/admin/get-documents-by-clinic.resolver';
import { feeScheduleResolver } from './core/resolvers/fee-schedule/fee-schedule.resolver';
import { feeScheduleProviderIdResolver } from './core/resolvers/fee-schedule/fee-schedule-provider-id.resolver';
import { getAllMembersResolver } from './core/resolvers/member/get-all-members.resolver';
import { getMemberByIdResolver } from './core/resolvers/member/get-member-by-id.resolver';
import { getClinicDataResolver } from './core/resolvers/admin/get-clinic-data.resolver';

export const routes: Routes = [
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'post-login'
    },
    {
        path: '',
        loadComponent: () => import('./features/home/home.component').then(m => m.HomeComponent),
        children: [

            {
                path: 'login',
                loadComponent: () => import('./features/authentication/login/login.component').then(m => m.LoginComponent)
            },
            {
                path: 'change-password',
                loadComponent: () => import('./features/authentication/change-password/change-password.component').then(m => m.ChangePasswordComponent)
            },
            {
                path: 'reset-password',
                loadComponent: () => import('./features/authentication/reset-password/reset-password.component').then(m => m.ResetPasswordComponent)
            },
            {
                path: 'post-login',
                loadComponent: () => import('./features/authentication/post-login/empty.component').then(m => m.EmptyComponent),
                canActivate: [postAuthGuard]
            },
            {
                path: 'not-authorized',
                loadComponent: () => import('./features/authentication/not-authorized/not-authorized.component').then(m => m.NotAuthorizedComponent)
            }
        ]
    },
    {
        path: 'admin',
        canActivate: [accessGuard],
        loadComponent: () => import('./features/admin/admin.component').then(m => m.AdminComponent),
        children: [
            {
                path: '',
                loadComponent: () => import('./features/admin/admin-home/admin-home.component').then(m => m.AdminHomeComponent)
            },
            {
                path: 'members',
                children: [
                    {
                        path: '', // The list view
                        resolve: { members: getAllMembersResolver },
                        loadComponent: () => import('./features/admin/members/members.component').then(m => m.MembersComponent)
                        
                    },
                    {
                        path: 'view/:member_id', // The detail view
                        resolve: { member: getMemberByIdResolver },
                        loadComponent: () => import('./features/admin/members/features/view/view.component').then(m => m.ViewComponent)
                        
                    }
                ]
            },
            {
                path: 'documents',
                loadComponent: () => import('./features/admin/documents/documents.component').then(m => m.DocumentsComponent)
            },
            {
                path: 'notes',
                loadComponent: () => import('./features/admin/notes/notes.component').then(m => m.NotesComponent)
            },
            {
                path: 'reporting',
                loadComponent: () => import('./features/admin/reporting/reporting.component').then(m => m.ReportingComponent)
            },
            {
                path: 'reporting2',
                resolve: {
                    members: getAllMembersResolver,
                    providers: getAllProvidersResolver
                },
                loadComponent: () => import('./features/admin/reporting2/reporting2.component').then(m => m.Reporting2Component)
            },
            {
                path: 'clinics',
                
                children: [
                    {
                        path: '',
                        resolve: [
                            getAllClinicsResolver,
                            getAllProvidersResolver
                        ],
                        loadComponent: () => import('./features/admin/clinics/clinic-list/clinic-list.component').then(m => m.ClinicListComponent)
                    },
                    {
                        path: 'view/:clinic_id',
                        resolve: [
                            getClinicDataResolver
                        ],
                        loadComponent: () => import('./features/admin/clinics/view-clinic/view-clinic.component').then(m => m.ViewClinicComponent)
                    }
                ]
                
            }
        ]
    },
    {
        path: 'onboarding',
        loadComponent: () => import('./features/onboarding/onboarding.component').then(m => m.OnboardingComponent),
        canMatch: [providerMatchGuard],
        canActivate: [providerOnboardingAccessGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                redirectTo: 'pending'
            },
            {
                path: 'demographics',
                canMatch: [onboardingDemographicsMatchGuard],
                resolve: {
                    demographics: clinicDemographicsResolver,
                },
                loadComponent: () => import('./features/onboarding/demographics/demographics.component').then(m => m.DemographicsComponent)
            },
            {
                path: 'locations',
                canMatch: [onboardingLocationsMatchGuard],
                resolve: {
                    demographics: clinicDemographicsResolver,
                },
                loadComponent: () => import('./features/onboarding/locations/locations.component').then(m => m.LocationsComponent)
            },
            {
                path: 'providers',
                canMatch: [onboardingProvidersMatchGuard],
                resolve: {
                    demographics: clinicDemographicsResolver,
                    providers: providersByClinicResolver,
                },
                loadComponent: () => import('./features/onboarding/providers/providers.component').then(m => m.ProvidersComponent)
            },
            {
                path: 'fee-schedule',
                canMatch: [onboardingFeeScheduleMatchGuard],
                resolve: {
                    feeSchedule: feeScheduleResolver
                },
                loadComponent: () => import('./features/onboarding/fee-schedule/fee-schedule.component').then(m => m.FeeScheduleComponent)
            },
            {
                path: 'documents',
                canMatch: [onboardingDocumentsMatchGuard],
                resolve: {
                    documents: providerDocumentResolver,
                    providers: providersByClinicResolver,
                },
                loadComponent: () => import('./features/onboarding/malpractice/malpractice.component').then(m => m.MalpracticeComponent)
            },
            {
                path: 'complete',
                canMatch: [onboardingCompleteMatchGuard],
                loadComponent: () => import('./features/onboarding/spalsh/spalsh.component').then(m => m.SpalshComponent)
            },
            {
                path: 'pending',
                canMatch: [onboardingPendingMatchGuard],
                loadComponent: () => import('./features/onboarding/waiting/waiting.component').then(m => m.WaitingComponent)
            },
        ]
    },
    {
        path: 'profile',
        loadComponent: () => import('./features/profile/profile.component').then(m => m.ProfileComponent),
        canMatch: [providerMatchGuard],
        canActivate: [providerAccessGuard],
        canActivateChild: [providerAccessGuard],
        children: [
            {
                path: '',
                loadComponent: () => import('./features/profile/home/home.component').then(m => m.HomeComponent),
            },
            // {
            //     path: 'ach',
            //     loadComponent: () => import('./features/profile/ach/ach.component').then(m => m.AchComponent)
            // },
            {
                path: 'member-lookup',
                loadComponent: () => import('./features/profile/member-lookup/member-lookup.component').then(m => m.MemberLookupComponent)
            },
            {
                path: 'clinics',
                resolve: {
                    demographics: clinicDemographicsResolver,
                    admin: clinicAdminResolver
                },
                loadComponent: () => import('./features/profile/clinic-information/clinic-information.component').then(m => m.ClinicInformationComponent)
            },
            {
                path: 'providers',
                resolve: {
                    demographics: clinicDemographicsResolver,
                    documents: providerDocumentResolver,
                    providersByClinicResolver
                },
                loadComponent: () => import('./features/profile/provider-information/provider-information.component').then(m => m.ProviderInformationComponent)
            },
            {
                path: 'documents',
                loadComponent: () => import('./features/profile/documents/documents.component').then(m => m.DocumentsComponent)
            },
            {
                path: 'fee-schedule',
                loadComponent: () => import('./features/profile/fee-schedule/fee-schedule.component').then(m => m.FeeScheduleComponent),
                resolve: {
                    demographics: clinicDemographicsResolver,
                    providersByClinicResolver
                },
                children : [
                    {
                        path: '',
                        redirectTo: 'providers',
                        pathMatch: 'full'
                    },
                    {
                        path: 'providers',
                        loadComponent: () => import('./features/profile/fee-schedule/providers/providers.component').then(m => m.ProvidersComponent)
                    },
                    {
                        path: 'view/:provider_id',
                        resolve: [
                            feeScheduleProviderIdResolver
                        ],
                        loadComponent: () => import('./features/profile/fee-schedule/view/view.component').then(m => m.ViewComponent)
                    }
                ]
            }
        ]
    },
    {
        path: 'member',
        loadComponent: () => import('./features/member/member.component').then(m => m.MemberComponent),
        canMatch: [memberMatchGuard],
        canActivate: [memberOnboardingAccessGuard],
        children: [
            {
                path: '',
                resolve: {
                    member: getMemberResolver   
                },
                loadComponent: () => import('./features/member/member-home/member-home.component').then(m => m.MemberHomeComponent),
            },
            {
                path: 'profile',
                
                resolve: {
                    member: getMemberResolver   
                },
                loadComponent: () => import('./features/member/member-profile/member-profile.component').then(m => m.MemberProfileComponent)
            },
            {
                path: 'faq',
                loadComponent: () => import('./features/member/documents/documents.component').then(m => m.DocumentsComponent)
            },
            {
                path: 'subscription',
                resolve: {
                    member: getMemberResolver
                },
                loadComponent: () => import('./features/member/subscription/subscription.component').then(m => m.SubscriptionComponent)
            },
            {
                path: 'onboarding',
                canActivate: [memberOnboardingMatchGuard],
                resolve: {
                    member: getMemberResolver,
                    providers: _getAllProvidersResolver   
                },
                loadComponent: () => import('./features/member/onboarding/onboarding.component').then(m => m.OnboardingComponent),
            },
        ]
    },
    {
        path: '**',
        loadComponent: () => import('./shared/components/not-found/not-found.component').then(m => m.NotFoundComponent)
    }
];
